import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { Comment } from '@common/schemas/comment.schema';
import { useCallback, useEffect, useState } from 'react';

/**
 * Hook to manage comments for a specific text content.
 */
export function useComments(textId: string | null) {
  const { user, auth, backend } = useFirebase();
  const { novel } = useNovel();
  const { userData } = useUserProfile();
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const layoutMode = novel?.storageLayout?.narrative?.contents?.items || 'embedded';

  useEffect(() => {
    if (!novel?.id || !novel?.ownerId || !textId || !user) {
      setComments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const unsubscribe = backend.comments.subscribeComments(
      novel.ownerId,
      novel.id,
      novel.storageLayout,
      textId,
      (docs) => {
        setComments(docs);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching comments:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [backend, novel?.ownerId, novel?.id, layoutMode, textId, user]);

  const addComment = useCallback(async (content: string) => {
    if (!novel.id || !textId || !auth?.currentUser) return null;
    return backend.comments.addComment(
      novel.ownerId,
      novel,
      textId,
      auth.currentUser.uid,
      userData?.displayName || auth.currentUser.displayName || 'Anonymous',
      content
    );
  }, [backend, novel, textId, auth, userData?.displayName]);

  const resolveComment = useCallback(async (commentId: string) => {
    if (!novel.id || !textId) return;
    await backend.comments.resolveComment(novel.ownerId, novel, textId, commentId);
  }, [backend, novel, textId]);

  const deleteComment = useCallback(async (commentId: string) => {
    if (!novel.id || !textId) return;
    await backend.comments.deleteComment(novel.ownerId, novel, textId, commentId);
  }, [backend, novel, textId]);

  const addReply = useCallback(async (commentId: string, content: string) => {
    if (!novel.id || !textId || !auth?.currentUser) return;
    const targetComment = comments.find((c) => c.id === commentId);
    if (!targetComment) return;

    await backend.comments.addReply(
      novel.ownerId,
      novel,
      textId,
      commentId,
      auth.currentUser.uid,
      userData?.displayName || auth.currentUser.displayName || 'Anonymous',
      content,
      targetComment.replies
    );
  }, [backend, novel, textId, auth, comments, userData?.displayName]);

  return {
    comments,
    isLoading,
    addComment,
    resolveComment,
    deleteComment,
    addReply,
  };
}
